import React from "react";
import {
  Box,
  Button,
  Typography,
  styled,
  Modal,
  TextField,
  Snackbar,
  Switch,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import {
  generateKeyPair,
  hashReportContent,
  signHash,
  exportPublicKeyToBase64,
  bufferToBase64,
  exportPrivateKeyToBase64,
} from "./hashComponent";
import { v4 as uuidv4 } from "uuid"; // Client-side UUID generation
import { supabase } from "../../lib/supabaseClient";
import { notifyCompany } from "./sendMail";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { notifyUser } from "../../lib/utils/notify.ts";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const PostComponent = ({ isGuest = false }) => {
  const [keyPair, setKeyPair] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [orgs, setOrgs] = React.useState([]);
  const [models, setModels] = React.useState([]);
  const [selectedOrg, setSelectedOrg] = React.useState("");
  const [selectedModel, setSelectedModel] = React.useState("");
  const [postAnonymously, setpostAnonymously] = React.useState(isGuest); // default ON for guests
  const [formData, setFormData] = React.useState({
    reportTitle: "",
    typeOfReport: "",
    summary: "",
  });
  const [uploadData, setUploadData] = React.useState(null);
  const [snackBarState, setSnackBarState] = React.useState({
    snackOpen: false,
    vertical: "top",
    horizontal: "center",
    message: "",
  });

  const [newOrgData, setNewOrgData] = React.useState({
    name: "",
    email: "",
    contact_url: "",
  });
  const [newModelData, setNewModelData] = React.useState({
    name: "",
    description: "",
  });

  const { vertical, horizontal, snackOpen } = snackBarState;

  const typeOptions = [
    "Prompt Injection",
    "Jailbreak",
    "Ethics/Bias",
    "Security Vulnerability",
    "Evaluation Feedback",
  ];

  const handleOpen = async () => {
    setOpen(true);
    // keep anonymous forced ON when opening in guest mode
    if (isGuest) setpostAnonymously(true);
    const { data, error } = await supabase.from("organizations").select("*");
    if (!error) setOrgs(data || []);
  };

  const handleOrgChange = async (e) => {
    const orgId = e.target.value;
    setSelectedOrg(orgId);
    setSelectedModel("");
    setModels([]);

    if (orgId && orgId !== "other") {
      const { data, error } = await supabase
        .from("models")
        .select("id, name")
        .eq("org_id", orgId);
      if (!error) setModels(data || []);
    }
  };

  // Keep anon ON for guests if auth/prop changes
  React.useEffect(() => {
    if (isGuest) setpostAnonymously(true);
  }, [isGuest]);

  // helper: read File -> base64 (no Storage read perms needed)
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result || "").toString().split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // --- Submit handler (guest-safe + anon enforced) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Gracefully handle no-session case (guest)
    let user = null;
    try {
      const res = await supabase.auth.getUser();
      user = res?.data?.user ?? null;
    } catch (_) {
      // no session => guest flow
    }
    const guest = isGuest || !user;

    const effectiveAnon = guest ? true : postAnonymously; // force anon for guests

    let finalOrgId = selectedOrg;
    let finalModelId = selectedModel;
    let summaryForDb = formData.summary;
    let newOrgDetailsForNotification = null;
    let newModelNameForNotification = null;

    if (selectedOrg === "other") {
      finalOrgId = null;
      finalModelId = null;
      newOrgDetailsForNotification = {
        name: newOrgData.name,
        email: newOrgData.email,
        contact_url: newOrgData.contact_url,
      };
      newModelNameForNotification = newModelData.name;
      summaryForDb = `[New Organization Submitted: ${newOrgData.name}]\n[New Model Submitted: ${newModelData.name}]\n\n---\n\n${formData.summary}`;
    } else if (selectedModel === "other") {
      finalModelId = null;
      newModelNameForNotification = newModelData.name;
      summaryForDb = `[New Model Submitted: ${newModelData.name}]\n\n---\n\n${formData.summary}`;
    }

    // Upload file (use neutral key when guest)
    let filePath = null;
    const file = uploadData?.[0] ?? null;
    let fileBase64 = null;

    try {
      if (file) {
        // 1) turn the selected file into base64 for email (works for guests)
        fileBase64 = await fileToBase64(file);

        // 2) upload to storage (requires INSERT policy for anon/auth)
        const keyPrefix = guest
          ? `guest_${Date.now()}`
          : `${user.id}_${Date.now()}`;
        const { data, error: uploadError } = await supabase.storage
          .from("report-files")
          .upload(`reports/${keyPrefix}_${file.name}`, file, {
            contentType: file.type,
          });

        if (uploadError) {
          setSnackBarState({
            snackOpen: true,
            vertical,
            horizontal,
            message: "Upload failed",
          });
          setIsSubmitting(false);
          return;
        }
        filePath = `report-files/${data.path}`;
      }
    } catch (_) {
      setSnackBarState({
        snackOpen: true,
        vertical,
        horizontal,
        message: "Upload failed",
      });
      setIsSubmitting(false);
      return;
    }

    // Anonymous signing artifacts (only when anon)
    let publicKeyBase64 = "",
      signatureBase64 = "",
      hashBuffer = "";
    const report_id = uuidv4();

    if (effectiveAnon === true) {
      const reportString = `${report_id}|${finalModelId || "N/A"}|${
        finalOrgId || "N/A"
      }|${formData.typeOfReport}`;
      hashBuffer = await hashReportContent(reportString);
      const pair = keyPair || (await generateKeyPair());
      setKeyPair(pair);
      const signature = await signHash(pair.privateKey, hashBuffer);
      publicKeyBase64 = await exportPublicKeyToBase64(pair.publicKey);
      signatureBase64 = bufferToBase64(signature);
    }

    // Insert the report (guest => null user/email); no .select() to avoid anon SELECT denial
    const { error: insertError } = await supabase.from("issue_reports").insert([
      {
        report_id,
        user_id: guest ? null : user.id,
        email: guest ? null : user.email,
        report_title: formData.reportTitle,
        summary: summaryForDb,
        type_of_report: formData.typeOfReport,
        model_id: finalModelId,
        org_id: finalOrgId,
        file_path: filePath,
        is_anonymous: effectiveAnon,
        signature: effectiveAnon ? signatureBase64 : "",
        public_key: effectiveAnon ? publicKeyBase64 : "",
      },
    ]);

    setSnackBarState({
      snackOpen: true,
      vertical,
      horizontal,
      message: insertError ? "Submission failed" : "Report submitted!",
    });

    // If DB insert failed, stop here (no cert, no emails)
    if (insertError) {
      setIsSubmitting(false);
      return;
    }

    // Download certificate
    if (effectiveAnon === true) {
      const reportHashBase64 = bufferToBase64(hashBuffer);
      const pair = keyPair || (await generateKeyPair());
      const privateKeyBase64 = await exportPrivateKeyToBase64(pair.privateKey);
      const certificate = {
        report_id,
        user_id: guest ? null : (user?.id ?? null),
        report_hash: reportHashBase64,
        public_key: publicKeyBase64,
        signature: signatureBase64,
        private_key: privateKeyBase64,
        created_at: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(certificate, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `securitypulse-cert-${Date.now()}.json`;
      a.click();
    } else {
      const certificate = { report_id };
      const blob = new Blob([JSON.stringify(certificate, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `securitypulse-cert-${Date.now()}.json`;
      a.click();
    }

    // Notifications (user if logged in; company always)
    try {
      if (!guest && user?.id) {
        await notifyUser({
          userId: user.id,
          title: "Report submitted",
          body: `Your report "${formData.reportTitle}" was received.`,
          type: "report_submitted",
          link: `/reports/${report_id}`,
          email: user.email || null,
        });
      }

      await notifyCompany({
        org_id: finalOrgId,
        model_id: finalModelId,
        report_title: formData.reportTitle,
        type_of_report: formData.typeOfReport,
        summary: formData.summary, // email original summary for clarity
        is_anonymous: effectiveAnon,
        email: guest ? null : (user?.email ?? null),
        new_org_details: newOrgDetailsForNotification,
        new_model_name: newModelNameForNotification,
        attachment: fileBase64
          ? {
              filename: file?.name,
              content: fileBase64,
              type: file?.type,
            }
          : undefined,
      });

      setSnackBarState({
        snackOpen: true,
        vertical,
        horizontal,
        message: "Report submitted! Company has been notified.",
      });
    } catch (err) {
      console.error("notify error:", err);
      setSnackBarState({
        snackOpen: true,
        vertical,
        horizontal,
        message: "Failed to notify company",
      });
    }

    // Reset form & close
    setFormData({ reportTitle: "", typeOfReport: "", summary: "" });
    setSelectedOrg("");
    setSelectedModel("");
    setUploadData(null);
    setpostAnonymously(isGuest ? true : false); // stay ON for guests
    setNewOrgData({ name: "", email: "", contact_url: "" });
    setNewModelData({ name: "", description: "" });
    setOpen(false);

    setIsSubmitting(false);
  };
  // --- end submit handler ---

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Submit a Report
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: 480 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6" align="center">
            Submit an Issue Report
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={postAnonymously}
                onChange={(e) => setpostAnonymously(e.target.checked)}
                disabled={isGuest} // lock in guest mode
              />
            }
            label="Submit anonymously"
          />

          <TextField
            label="Report Title"
            value={formData.reportTitle}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, reportTitle: e.target.value }))
            }
            fullWidth
            required
          />

          <TextField
            select
            label="Select Company"
            value={selectedOrg}
            onChange={handleOrgChange}
            fullWidth
            required
          >
            {orgs.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
            <MenuItem key="other" value="other">
              Other...
            </MenuItem>
          </TextField>

          {selectedOrg === "other" && (
            <>
              <TextField
                label="New Organization Name"
                value={newOrgData.name}
                onChange={(e) =>
                  setNewOrgData((prev) => ({ ...prev, name: e.target.value }))
                }
                fullWidth
                required
              />
              <TextField
                label="Security Email"
                type="email"
                value={newOrgData.email}
                onChange={(e) =>
                  setNewOrgData((prev) => ({ ...prev, email: e.target.value }))
                }
                fullWidth
                required
              />
              <TextField
                label="Contact URL"
                type="url"
                value={newOrgData.contact_url}
                onChange={(e) =>
                  setNewOrgData((prev) => ({
                    ...prev,
                    contact_url: e.target.value,
                  }))
                }
                fullWidth
                required
              />
            </>
          )}

          {selectedOrg && selectedOrg !== "other" && (
            <TextField
              select
              label="Select Model"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              fullWidth
              required
              disabled={!selectedOrg}
            >
              {models.map((model) => (
                <MenuItem key={model.id} value={model.id}>
                  {model.name}
                </MenuItem>
              ))}
              <MenuItem key="other" value="other">
                Other...
              </MenuItem>
            </TextField>
          )}

          {(selectedOrg === "other" || selectedModel === "other") && (
            <>
              <TextField
                label="New Model Name"
                value={newModelData.name}
                onChange={(e) =>
                  setNewModelData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label="Model Description"
                value={newModelData.description}
                onChange={(e) =>
                  setNewModelData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                fullWidth
                required
                multiline
                rows={2}
              />
            </>
          )}

          <TextField
            select
            label="Type of Report"
            value={formData.typeOfReport}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                typeOfReport: e.target.value,
              }))
            }
            fullWidth
            required
          >
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Summary of Findings"
            value={formData.summary}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                summary: e.target.value,
              }))
            }
            fullWidth
            multiline
            rows={4}
            margin="normal"
            placeholder="Provide a detailed summary of your findings..."
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
            >
              {uploadData?.[0]?.name || "Upload File (PDF, DOC, DOCX, TXT)"}
              <VisuallyHiddenInput
                type="file"
                accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  const allowedTypes = [
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "text/plain",
                  ];
                  if (
                    file &&
                    !allowedTypes.some((type) => file.type === type)
                  ) {
                    alert("Only PDF, DOC, DOCX, and TXT files are allowed.");
                    e.target.value = ""; // Clear the file input
                    return;
                  }
                  setUploadData(e.target.files);
                }}
              />
            </Button>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={snackOpen}
        onClose={() =>
          setSnackBarState((prev) => ({ ...prev, snackOpen: false }))
        }
        message={snackBarState.message}
        autoHideDuration={3000}
      />
    </>
  );
};

export default PostComponent;
